import {
  Component,
  OnInit,
  HostListener,
  ElementRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { NavigationService } from '../../../services/navigation.service';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { AuthService } from '../../../services/auth.service';
import { RoleService } from '../../../services/role.service';

/**
 * Header component for the Super Admin area providing navigation controls, profile display, and logout behaviour.
 * @component
 * @selector app-super-admin-header
 * @example
 *   <app-super-admin-header></app-super-admin-header>
 * @input none - This component does not accept any @Input properties; it derives state from injected services and localStorage.
 * @output none - This component does not emit @Output events; interactions are handled via services and navigation.
 *
 * Purpose:
 *   - Displays the super-admin header with responsive navigation controls, profile initials/email, and a profile dropdown.
 *   - Synchronizes its open/collapsed state with a shared NavigationService and reads persisted super-admin info from localStorage.
 *
 * Injected services:
 *   - NavigationService: subscribes to isMobileNavOpen$ and isSidebarCollapsed$ and calls toggleMobileNav() to change global navigation state.
 *   - ElementRef: used to determine clicks outside the component to close the profile dropdown.
 *   - Router: used to navigate to the super-admin login page on logout.
 *   - AuthService: clears authentication state on logout.
 *   - RoleService: clears stored role information on logout.
 *
 * Internal state (key properties):
 *   - isMobileNavOpen: boolean - whether the mobile navigation panel is open (kept in sync with NavigationService).
 *   - isSidebarCollapsed: boolean - whether the sidebar is collapsed (kept in sync with NavigationService).
 *   - isProfileDropdownOpen: boolean - whether the profile dropdown is currently open.
 *   - userEmail: string - the logged-in super-admin email/username loaded from localStorage.
 *   - userInitials: string - initials generated from the email/username for avatar display.
 *
 * Key methods:
 *   - ngOnInit(): void
 *       - Lifecycle hook that initializes component state by calling loadSuperAdminData().
 *   - ngOnDestroy(): void
 *       - Lifecycle hook reserved for cleanup (subscriptions handled by services in this component; placeholder for future teardown).
 *   - private loadSuperAdminData(): void
 *       - Reads 'superAdminData' from localStorage (if available), safely parses it, and extracts username/email and initials.
 *       - Handles parsing errors and falls back to empty values when necessary.
 *   - toggleMobileNav(): void
 *       - Delegates to NavigationService.toggleMobileNav() to open/close the mobile navigation panel.
 *   - toggleProfileDropdown(): void
 *       - Toggles the profile dropdown open state and ensures mobile nav is closed when the dropdown opens.
 *   - logout(): void
 *       - Clears super-admin and user data from localStorage, resets AuthService user state, clears roles via RoleService,
 *         navigates to '/admin/login', and closes the profile dropdown.
 *   - isMobileView(): boolean
 *       - Returns true when the window inner width is <= 880px to help with responsive UI decisions.
 *
 * Important lifecycle/event behavior:
 *   - Subscribes to NavigationService.isMobileNavOpen$ and isSidebarCollapsed$ in the constructor to keep header state synchronized.
 *   - Uses a HostListener on document click to detect clicks outside the component (via ElementRef) and close the profile dropdown.
 *   - Persists no new data itself but reads persistent super-admin metadata from localStorage ('superAdminData').
 */
@Component({
  selector: 'app-super-admin-header',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './super-admin-header.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./super-admin-header.component.scss'],
})
export class SuperAdminHeaderComponent implements OnInit {
  isMobileNavOpen = false;
  isSidebarCollapsed = false;
  isProfileDropdownOpen = false;
  userEmail = '';
  userInitials = '';

  /**
   * Instantiates the SuperAdminHeaderComponent and subscribes to navigation observables to update mobile nav and sidebar state.
   * @example
   * new SuperAdminHeaderComponent(navigationService, elementRef, router, authService, roleService)
   * SuperAdminHeaderComponent instance
   * @param {{NavigationService}} {{navigationService}} - Service used to observe and control navigation state (mobile nav open, sidebar collapsed).
   * @param {{ElementRef}} {{elementRef}} - Reference to the host DOM element for the component.
   * @param {{Router}} {{router}} - Router used for navigation within the application.
   * @param {{AuthService}} {{authService}} - Service used to access authentication and current user details.
   * @param {{RoleService}} {{roleService}} - Service used to query and manage user roles.
   * @returns {{SuperAdminHeaderComponent}} Instance of SuperAdminHeaderComponent.
   **/
  constructor(
    private navigationService: NavigationService,
    private elementRef: ElementRef,
    private router: Router,
    private authService: AuthService,
    private roleService: RoleService
  ) {
    this.navigationService.isMobileNavOpen$.subscribe(isOpen => {
      this.isMobileNavOpen = isOpen;
    });

    this.navigationService.isSidebarCollapsed$.subscribe(collapsed => {
      this.isSidebarCollapsed = collapsed;
    });
  }

  ngOnInit(): void {
    this.loadSuperAdminData();
  }

  /**
   * Load super admin data from localStorage, parse it, and populate this.userEmail and this.userInitials.
   * @example
   * loadSuperAdminData()
   * undefined
   * @param {void} none - No arguments.
   * @returns {void} Does not return a value.
   **/
  private loadSuperAdminData(): void {
    if (typeof window !== 'undefined') {
      const superAdminData = localStorage.getItem('superAdminData');
      if (superAdminData) {
        try {
          const parsedData = JSON.parse(superAdminData);
          this.userEmail = parsedData.username || parsedData.email || '';
          // Generate initials from email
          if (this.userEmail) {
            const parts = this.userEmail.split('@')[0].split('.');
            if (parts.length >= 2) {
              this.userInitials = (parts[0][0] + parts[1][0]).toUpperCase();
            } else {
              this.userInitials = this.userEmail.substring(0, 2).toUpperCase();
            }
          }
        } catch (error) {
          console.error('Error parsing superAdminData:', error);
        }
      }
    }
  }

  toggleMobileNav() {
    this.navigationService.toggleMobileNav();
  }

  toggleProfileDropdown() {
    this.isProfileDropdownOpen = !this.isProfileDropdownOpen;
    if (this.isProfileDropdownOpen) {
      this.isMobileNavOpen = false;
    }
  }

  /**
   * Logs out the current super-admin by clearing stored authentication data, resetting authentication and role state, navigating to the admin login page, and closing the profile dropdown.
   * @example
   * ogout()
   * // undefined
   * @returns {void} No return value.
   **/
  logout(): void {
    // Clear all authentication data
    localStorage.removeItem('superAdminData');
    localStorage.removeItem('userData');

    // Clear auth service state
    this.authService.user.set(null);

    // Clear role service
    this.roleService.clearUserRole();

    // Navigate to super admin login
    this.router.navigate(['/admin/login']);
    this.isProfileDropdownOpen = false;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.isProfileDropdownOpen = false;
    }
  }

  isMobileView(): boolean {
    return window.innerWidth <= 880;
  }
}
